/**
 * CreateAppJobModal Component
 *
 * Modal for creating a new appJob.
 * App jobs use service accounts for execution identity.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { Checkbox } from '../../../components/ui/Checkbox';
import { FormField } from '../../../components/forms/FormField';
import { useCreateAppJob } from '../hooks/useCreateAppJob';
import { useServiceAccounts } from '../../service-accounts/hooks/useServiceAccounts';
import { useApplications } from '../../applications/hooks/useApplications';
import {
  createAppJobFormSchema,
  parsePayload,
  commonTimezones,
  cronPresets,
  type CreateAppJobFormData,
} from '../../../lib/validators/app-job';
import { RoleType } from '../../../types/role.types';

interface CreateAppJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * Generate code from name
 * Converts "My Schedule Name" to "my-appJob-name"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

/**
 * CreateAppJobModal component
 */
export const CreateAppJobModal: React.FC<CreateAppJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  // Fetch applications
  const { data: applications = [], isLoading: isLoadingApps } = useApplications(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CreateAppJobFormData>({
    resolver: zodResolver(createAppJobFormSchema),
    defaultValues: {
      applicationId: '',
      appJobCode: '',
      appJobName: '',
      description: '',
      serviceAccountId: '',
      functionName: '',
      payloadInput: '',
      cronExpression: '0 0 * * *',
      timezone: 'UTC',
    },
  });

  const selectedApplicationId = watch('applicationId');
  const watchName = watch('appJobName');

  // Auto-generate code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('appJobCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  // Fetch all service accounts for tenant
  const { data: allServiceAccounts = [], isLoading: isLoadingServiceAccounts } = useServiceAccounts(tenantId);

  // Filter service accounts that have APP_DOMAIN roles with permissions for selected application
  const eligibleServiceAccounts = useMemo(() => {
    if (!selectedApplicationId || !applications.length) return [];
    const app = applications.find(a => a.applicationId === selectedApplicationId);
    if (!app) return [];

    return allServiceAccounts.filter(sa => {
      // Service account must have at least one APP_DOMAIN role
      const appDomainRoles = (sa.roles || []).filter(r => r.type === RoleType.APP_DOMAIN);
      if (appDomainRoles.length === 0) return false;

      // Check if any APP_DOMAIN role has permissions for this application
      // Note: We can't check full permissions on the frontend since RoleAssignment
      // only has basic info. The backend will do full validation.
      // For now, we just check if they have any APP_DOMAIN roles.
      return true;
    });
  }, [allServiceAccounts, selectedApplicationId, applications]);

  // Reset service account when application changes
  useEffect(() => {
    setValue('serviceAccountId', '');
  }, [selectedApplicationId, setValue]);

  const { mutate: createAppJob, isPending } = useCreateAppJob(tenantId, {
    onSuccess: () => {
      setSuccessMessage('App job created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        setAutoGenerateCode(true);
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateAppJobFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Parse payload if provided
    let payload: Record<string, unknown> | undefined;
    if (data.payloadInput?.trim()) {
      try {
        payload = parsePayload(data.payloadInput);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Invalid JSON payload');
        return;
      }
    }

    createAppJob({
      applicationId: data.applicationId,
      data: {
        appJobCode: data.appJobCode,
        appJobName: data.appJobName,
        description: data.description || undefined,
        serviceAccountId: data.serviceAccountId,
        functionName: data.functionName,
        payload,
        cronExpression: data.cronExpression,
        timezone: data.timezone,
      },
    });
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      onClose();
    }
  };

  const handleCronPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      setValue('cronExpression', value);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create App Job</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoadingApps && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {/* Content */}
        {!isLoadingApps && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Info Alert */}
            <Alert variant="info">
              App jobs define recurring execution of application functions using cron expressions.
              A service account with APP_DOMAIN roles is required to define execution permissions.
            </Alert>

            {/* Error Message */}
            {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

            {/* Success Message */}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            {/* Application */}
            <FormField
              label="Application"
              required
              error={errors.applicationId?.message}
              helperText="The application containing the function to execute"
            >
              <Select {...register('applicationId')} error={!!errors.applicationId}>
                <option value="">Select application</option>
                {applications.map((app) => (
                  <option key={app.applicationId} value={app.applicationId}>
                    {app.applicationName} ({app.applicationCode})
                  </option>
                ))}
              </Select>
            </FormField>

            {/* Service Account */}
            <FormField
              label="Service Account"
              required
              error={errors.serviceAccountId?.message}
              helperText="The service account that defines execution identity and permissions"
            >
              {isLoadingServiceAccounts ? (
                <div className="flex items-center gap-2 py-2">
                  <Spinner size="sm" />
                  <span className="text-sm text-gray-500">Loading service accounts...</span>
                </div>
              ) : (
                <Select
                  {...register('serviceAccountId')}
                  error={!!errors.serviceAccountId}
                  disabled={!selectedApplicationId}
                >
                  <option value="">Select service account</option>
                  {eligibleServiceAccounts.map((sa) => (
                    <option key={sa.serviceAccountId} value={sa.serviceAccountId}>
                      {sa.serviceAccountName} ({sa.serviceAccountCode})
                    </option>
                  ))}
                </Select>
              )}
              {selectedApplicationId && eligibleServiceAccounts.length === 0 && !isLoadingServiceAccounts && (
                <p className="text-sm text-amber-600 mt-1">
                  No service accounts found with APP_DOMAIN roles. Create a service account and assign APP_DOMAIN roles first.
                </p>
              )}
            </FormField>

            {/* Schedule Name */}
            <FormField
              label="Name"
              required
              error={errors.appJobName?.message}
              helperText="Display name for the app job"
            >
              <Input
                {...register('appJobName')}
                placeholder="Nightly Report"
                error={!!errors.appJobName}
              />
            </FormField>

            {/* Schedule Code */}
            <FormField
              label="Code"
              required
              error={errors.appJobCode?.message}
            >
              <Input
                {...register('appJobCode')}
                placeholder="nightly-report"
                error={!!errors.appJobCode}
                disabled={autoGenerateCode}
              />
              <div className="mt-2 flex items-center">
                <Checkbox
                  id="autoGenerateScheduleCode"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                />
                <label htmlFor="autoGenerateScheduleCode" className="ml-2 text-sm text-gray-600">
                  Auto-generate code from name
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
              </p>
            </FormField>

            {/* Function Name */}
            <FormField
              label="Function Name"
              required
              error={errors.functionName?.message}
              helperText="The exported function to execute"
            >
              <Input
                {...register('functionName')}
                placeholder="generateReport"
                error={!!errors.functionName}
              />
            </FormField>

            {/* Cron Expression */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Cron Preset" helperText="Quick select common patterns">
                <Select onChange={handleCronPresetChange} defaultValue="">
                  <option value="">Custom</option>
                  {cronPresets.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField
                label="Cron Expression"
                required
                error={errors.cronExpression?.message}
                helperText="minute hour day month weekday"
              >
                <Input
                  {...register('cronExpression')}
                  placeholder="0 0 * * *"
                  error={!!errors.cronExpression}
                />
              </FormField>
            </div>

            {/* Timezone */}
            <FormField
              label="Timezone"
              required
              error={errors.timezone?.message}
              helperText="IANA timezone for app job execution"
            >
              <Select {...register('timezone')} error={!!errors.timezone}>
                {commonTimezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </Select>
            </FormField>

            {/* Payload */}
            <FormField
              label="Payload (JSON)"
              error={errors.payloadInput?.message}
              helperText="Optional JSON object to pass to the function"
            >
              <Textarea
                {...register('payloadInput')}
                placeholder='{"key": "value"}'
                rows={3}
                error={!!errors.payloadInput}
              />
            </FormField>

            {/* Description */}
            <FormField label="Description" error={errors.description?.message}>
              <Textarea
                {...register('description')}
                placeholder="Optional description for this app job..."
                rows={2}
                error={!!errors.description}
              />
            </FormField>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isPending}
                disabled={applications.length === 0 || eligibleServiceAccounts.length === 0}
              >
                Create App Job
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};
